import { useEffect, useReducer, useState } from 'react';
import { usePost } from 'http/useApi';
import { rqPost } from 'http/apiRoutes';
import useWindowsDimension from 'functions/useWindowsDimension';
import {
  Grid,
  Box,
  List,
  ListItem,
  ListItemButton,
  Button,
  Switch,
  Typography,
  Checkbox,
  ListItemText,
} from '@mui/material';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import { useAtom } from 'jotai';
import userAtom from 'data/atoms/userAtom';
import { useTranslation } from 'react-i18next';
import { SearchFilter } from 'components/ui/SearchFilter';

import _ from 'lodash';

import {
  ICompanyViewModel,
  IDepartmentViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';
import {
  CloudFactoryEmployeeListViewModel,
  CloudFactoryEmployeeViewModel,
  IImportEmployeeAddViewModel,
} from './Converts/ICloudFactoryInterfaces';

export interface IImportEmployeesInterface {
  employees: IImportEmployeeAddViewModel[];
  selectedEmployees: string[];
  primaryContacts: string[];
}

const initialState: IImportEmployeesInterface = {
  employees: [],
  selectedEmployees: [],
  primaryContacts: [],
};

type Action =
  | { type: 'RESET_FORM' }
  | { type: 'SET_EMPLOYEES'; payload: IImportEmployeeAddViewModel[] }
  | { type: 'REMOVE_EMPLOYEE'; payload: string[] }
  | { type: 'TOGGLE_EMPLOYEE_SELECTION'; payload: string }
  | { type: 'TOGGLE_PRIMARY_CONTACT'; payload: string };

const reducer = (
  state: IImportEmployeesInterface,
  action: Action
): IImportEmployeesInterface => {
  switch (action.type) {
    case 'RESET_FORM':
      return { ...initialState };
    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload };
    case 'REMOVE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.filter(
          (emp) => !action.payload.includes(emp.email)
        ),
        selectedEmployees: [],
        primaryContacts: [],
      };
    case 'TOGGLE_EMPLOYEE_SELECTION':
      return {
        ...state,
        selectedEmployees: state.selectedEmployees.includes(action.payload)
          ? state.selectedEmployees.filter((email) => email !== action.payload)
          : [...state.selectedEmployees, action.payload],
      };
    case 'TOGGLE_PRIMARY_CONTACT':
      return {
        ...state,
        primaryContacts: state.primaryContacts.includes(action.payload)
          ? state.primaryContacts.filter((email) => email !== action.payload)
          : [...state.primaryContacts, action.payload],
      };
    default:
      return state;
  }
};

type Props = {
  expanded: boolean;
  employees: IImportEmployeeAddViewModel[];
  company: ICompanyViewModel | null;
  departments: IDepartmentViewModel[];
  handleRemoveEmployees: (employees: CloudFactoryEmployeeViewModel[]) => void;
};

export const ImportEmployeesList = ({
  expanded,
  employees,
  departments,
  company,
  handleRemoveEmployees,
}: Props) => {
  const { t } = useTranslation();
  const { height } = useWindowsDimension();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [user] = useAtom(userAtom);

  const postEmployee = usePost(
    rqPost.importEmployees(company?.id || '', true),
    
  );

  useEffect(() => {
    // Alltid clear når employees endres (inkl. når drawer åpnes og vi sender inn tom liste)
    dispatch({ type: 'RESET_FORM' });

    // Hvis vi har nye ansatte -> sett de inn
    if (employees && employees.length > 0) {
      dispatch({
        type: 'SET_EMPLOYEES',
        payload: _.cloneDeep(employees),
      });
    }
  }, [employees]);

  const listHeight = height - 238;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id) return;

    const selected = state.selectedEmployees;
    const employeeList: CloudFactoryEmployeeListViewModel = {
      numberOfCloudFactoryEmployees: 0,
      cloudFactoryEmployees: [],
    };

    selected.forEach((email) => {
      const emp = state.employees.find((e) => e.email === email);
      if (emp) {
        employeeList.cloudFactoryEmployees.push({
          name: emp.name,
          companyId: company.id,
          creatorId: user.id || '',
          phone: '+47 000 00 000',
          email: emp.email,
          departmentId: departments[0]?.id || null,
          primaryContact: state.primaryContacts.includes(emp.email),
          legacyId: emp.legacyId,
          thirdPartyId: emp.thirdPartyId,
          thirdParty: ThirdPartEnum.CloudFactory,
          productIds: emp.productIds || [],
          isEmailFoundInBluu: false,
          isNotEmailInCloudFactory: true,
        } as CloudFactoryEmployeeViewModel);
      }
    });

    employeeList.numberOfCloudFactoryEmployees =
      employeeList.cloudFactoryEmployees.length;
    dispatch({ type: 'REMOVE_EMPLOYEE', payload: selected });

    postEmployee.mutate(employeeList, {
      onSuccess: (res: any) => handleRemoveEmployees(res.allEmployees || []),
      onError: () => {},
    });
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <SearchFilter
          Icon={FeedRoundedIcon}
          Header={t('company.employees.detailsHeader')}
        />
      </Grid>
      <List
        sx={{
          width: '100%',
          height: listHeight,
          p: 1,
          borderRadius: 0,
          overflow: 'auto',
          backgroundColor: 'primary.main',
        }}
      >
        {state.employees.map(
          (employee) =>
            !employee.isEmailFoundInBluu && (
              <ListItemButton
                key={employee.email}
                onClick={() =>
                  dispatch({
                    type: 'TOGGLE_EMPLOYEE_SELECTION',
                    payload: employee.email,
                  })
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: state.selectedEmployees.includes(
                    employee.email
                  )
                    ? 'success.dark'
                    : 'primary.dark',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <ListItemText
                    primary={employee.name}
                    secondary={employee.email}
                    primaryTypographyProps={{ color: 'white' }}
                    secondaryTypographyProps={{ color: 'white' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      fontSize: 14,
                      color: 'primary.text',
                    }}
                  >
                    {t('company.employees.primaryContact').toUpperCase()}
                  </Typography>
                  <Switch
                    checked={state.primaryContacts.includes(employee.email)}
                    color={
                      state.primaryContacts.includes(employee.email)
                        ? 'secondary'
                        : 'primary'
                    }
                    onChange={() =>
                      dispatch({
                        type: 'TOGGLE_PRIMARY_CONTACT',
                        payload: employee.email,
                      })
                    }
                  />
                </Box>
                <Checkbox
                  checked={state.selectedEmployees.includes(employee.email)}
                  color={
                    state.selectedEmployees.includes(employee.email)
                      ? 'secondary'
                      : 'primary'
                  }
                  onChange={() =>
                    dispatch({
                      type: 'TOGGLE_EMPLOYEE_SELECTION',
                      payload: employee.email,
                    })
                  }
                />
              </ListItemButton>
            )
        )}
      </List>
      <Grid item xs={12} sx={{ p: 1, backgroundColor: 'primary.main' }}>
        <Button onClick={onSubmit} variant='contained'>
          {postEmployee.isLoading ? t('button.creating') : t('button.create')}
        </Button>
      </Grid>
    </Grid>
  );
};
